import { useI18n } from "@wojo/localization";
import { Text } from "@wojo/ui";
import React from "react";
import { useTourPageContext } from "../../../_client/context";
import { WorkHours } from "../../../_client/context/tour-page-context/TourPageContext";

type WorkHoursWithLabels = {
    workHours: WorkHours[] | undefined;
    label?: string | undefined;
};

export const ContactInformation: React.FC = () => {
    const { contactConfig } = useTourPageContext();
    const { global } = useI18n("global");
    const { productPage } = useI18n("productPage");

    if (!contactConfig) {
        return null;
    }

    function formatDay(
        day: WorkHoursWithLabels,
        label: string | undefined = day.label,
    ) {
        const { isClosed, startTime, endTime } = day.workHours?.[0] || {};

        const hasHoursToDisplay = isClosed || (startTime && endTime);
        if (hasHoursToDisplay) {
            const hours = isClosed
                ? productPage.tripBuilder.contactUs.closedLabel.l()
                : `${startTime}–${endTime}`;
            return `${label}, ${hours}`;
        }
    }

    const {
        workHoursMonday,
        workHoursTuesday,
        workHoursWednesday,
        workHoursThursday,
        workHoursFriday,
        workHoursSaturday,
        workHoursSunday,
    } = contactConfig;

    const weekdayHours: WorkHoursWithLabels[] = [
        { workHours: workHoursMonday, label: global.daysOfWeek.Monday.l() },
        { workHours: workHoursTuesday, label: global.daysOfWeek.Tuesday.l() },
        {
            workHours: workHoursWednesday,
            label: global.daysOfWeek.Wednesday.l(),
        },
        { workHours: workHoursThursday, label: global.daysOfWeek.Thursday.l() },
        { workHours: workHoursFriday, label: global.daysOfWeek.Friday.l() },
    ];

    const weekendHours: WorkHoursWithLabels[] = [
        { workHours: workHoursSaturday, label: global.daysOfWeek.Saturday.l() },
        { workHours: workHoursSunday, label: global.daysOfWeek.Sunday.l() },
    ];

    const hasSameHours = (days: WorkHoursWithLabels[]) =>
        days.every((day) => {
            const currentDay = day.workHours?.[0];
            const firstDay = days[0]?.workHours?.[0];
            return (
                currentDay?.startTime === firstDay?.startTime &&
                currentDay?.endTime === firstDay?.endTime &&
                currentDay?.isClosed === firstDay?.isClosed
            );
        });

    let hoursOfOperation: (string | undefined)[] = [];

    const buildHoursOfOperation = (
        hours: WorkHoursWithLabels[],
        label: string,
    ) => {
        if (hasSameHours(hours)) {
            hoursOfOperation.push(formatDay(hours[0], label));
        } else {
            hoursOfOperation.push(
                ...hours.map((day) => formatDay(day, day.label)),
            );
        }
    };

    buildHoursOfOperation(weekdayHours, global.weekdays.l());
    buildHoursOfOperation(weekendHours, global.weekends.l());

    return (
        <>
            <Text.Heading
                order="5"
                tag="p"
                style={{
                    marginTop: "var(--g-spacing-md)",
                    marginBottom: "var(--g-spacing-xs)",
                }}
            >
                {contactConfig.phoneNumberFormatted}
            </Text.Heading>
            {hoursOfOperation.map((hours, i) => (
                <Text.Body key={`${hours}-${i}`}>{hours}</Text.Body>
            ))}
        </>
    );
};
